import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, post, postForm, put, withQuery } from "./client";
import { SITES_KEY } from "./sites";

/** One line of a site's readiness panel: a record read from elsewhere, or an
 * item the office confirms. */
export interface ReadinessLine {
  key: string;
  label: string;
  source: "record" | "office";
  state: "open" | "confirmed" | "not_needed";
  by: string | null;
  at: string | null;
  note: string | null;
  reference: string | null;
}

export interface OverrideView {
  id: number;
  active: boolean;
  granted_at: string | null;
  granted_by: string | null;
  role: string | null;
  reason: string;
  evidence_kind: "email" | "emergency_qcp";
  document_url: string;
  revoked_at: string | null;
  revoked_by: string | null;
  revoke_reason: string | null;
}

export interface Readiness {
  site_id: number;
  site_name: string;
  work_order_type: "PM41" | "PM42" | null;
  journey: { kind: "pm41" | "pm42"; basis: "default" | "work_order" | "inspection"; inspection_id: number | null };
  gate: { enabled: boolean; applies: boolean; passes: boolean; reasons: string[] };
  items: ReadinessLine[];
  override: OverrideView | null;
  can_override: boolean;
}

export interface OverrideWeekRow extends OverrideView {
  site: { id: number; name: string; rs_no: string | null; notif_no: string | null };
  scheduled: { plan_id: number; plan_name: string; date: string | null } | null;
  po_status: string;
}

export interface OverrideWeek {
  monday: string;
  sunday: string;
  prev_week: string;
  next_week: string;
  rows: OverrideWeekRow[];
  active_total: number;
}

export const EVIDENCE_LABEL: Record<OverrideView["evidence_kind"], string> = { email: "E-mail", emergency_qcp: "Emergency QCP" };

export const readinessKey = (siteId: number) => ["readiness", siteId] as const;

export function useReadiness(siteId: number) {
  return useQuery<Readiness, ApiError>({ queryKey: readinessKey(siteId), queryFn: () => get(`/api/v1/sites/${siteId}/readiness`) });
}

function useReadinessWrite<TVars>(siteId: number, write: (vars: TVars) => Promise<Readiness>) {
  const client = useQueryClient();
  return useMutation<Readiness, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (body) => {
      client.setQueryData(readinessKey(siteId), body);
      void client.invalidateQueries({ queryKey: ["routing"] });
      void client.invalidateQueries({ queryKey: SITES_KEY });
      void client.invalidateQueries({ queryKey: ["overrides"] });
    },
  });
}

export function useSetReadinessItem(siteId: number) {
  return useReadinessWrite(siteId, (vars: { item: string; state: ReadinessLine["state"]; note: string | null }) =>
    put<Readiness>(`/api/v1/sites/${siteId}/readiness/${vars.item}`, { state: vars.state, note: vars.note }),
  );
}

export function useSetWorkOrderType(siteId: number) {
  return useReadinessWrite(siteId, (vars: { work_order_type: string | null; note: string | null }) =>
    put<Readiness>(`/api/v1/sites/${siteId}/work-order-type`, vars),
  );
}

export function useGrantOverride(siteId: number) {
  return useReadinessWrite(siteId, (vars: { reason: string; evidence_kind: OverrideView["evidence_kind"]; file: File }) => {
    const form = new FormData();
    form.append("reason", vars.reason);
    form.append("evidence_kind", vars.evidence_kind);
    form.append("file", vars.file, vars.file.name);
    return postForm<Readiness>(`/api/v1/sites/${siteId}/emergency-override`, form);
  });
}

export function useRevokeOverride(siteId: number) {
  return useReadinessWrite(siteId, (reason: string) => post<Readiness>(`/api/v1/sites/${siteId}/emergency-override/revoke`, { reason }));
}

export function useOverrideWeek(week: string | null) {
  return useQuery<OverrideWeek, ApiError>({
    queryKey: ["overrides", week],
    queryFn: () => get(withQuery("/api/v1/routing/overrides", { week })),
  });
}
